import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ParticipantAuthProfile } from '../api/types'
import type { RefreshOutcome } from '../api/client'
import {
  apiUrl,
  bootstrapSession,
  setSession,
  clearSession as clearApiSession,
  getSessionToken,
  getSessionRoles,
  setActiveSessionRole,
  getParticipantId,
} from '../api/client'
import { queryClient } from '../api/queryClient'

const AUTH_STATE_STORAGE_KEY = 'osle.web.auth-state'

interface AuthState {
  token: string | null
  participant: ParticipantAuthProfile | null
  roles: string[]
  activeRole: string | null
}

interface AuthContextValue extends AuthState {
  login: (accessToken: string, participant: ParticipantAuthProfile) => void
  logout: () => void
  switchRole: (role: string) => void
  syncParticipant: (participant: ParticipantAuthProfile) => void
  isAuthenticated: boolean
  /**
   * True until the refresh cookie has been asked whether a session exists.
   *
   * Tokens are memory-only (Rule 14), so every reload begins signed out and has to
   * ask. Gates on `isAuthenticated` must wait for this or they will bounce a
   * signed-in user off their own page on every refresh.
   */
  isBootstrapping: boolean
}

const initialState: AuthState = {
  token: null,
  participant: null,
  roles: [],
  activeRole: null,
}

interface StoredAuthState {
  participant: ParticipantAuthProfile | null
  activeRole: string | null
}

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function readStoredAuthState(): StoredAuthState | null {
  if (!canUseStorage()) return null
  const raw = window.localStorage.getItem(AUTH_STATE_STORAGE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as StoredAuthState
  } catch {
    window.localStorage.removeItem(AUTH_STATE_STORAGE_KEY)
    return null
  }
}

function persistAuthState(state: StoredAuthState) {
  if (!canUseStorage()) return
  window.localStorage.setItem(AUTH_STATE_STORAGE_KEY, JSON.stringify(state))
}

function clearStoredAuthState() {
  if (!canUseStorage()) return
  window.localStorage.removeItem(AUTH_STATE_STORAGE_KEY)
}

export const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  login: () => {},
  logout: () => {},
  switchRole: () => {},
  syncParticipant: () => {},
  isAuthenticated: false,
  isBootstrapping: false,
})

export function useAuth() {
  return useContext(AuthContext)
}

export function useAuthState(): AuthContextValue {
  // Always starts empty: nothing survives a reload in memory, and the refresh cookie
  // is unreadable from here, so the only way to know is to ask the server below.
  const [state, setState] = useState<AuthState>(initialState)
  const [isBootstrapping, setBootstrapping] = useState(true)
  // Set as soon as a real login lands, so a bootstrap that resolves afterwards
  // (signing in is faster than the round trip on a slow link) does not tear down
  // the session it raced.
  const signedInDuringBootstrap = useRef(false)

  const login = useCallback((accessToken: string, participant: ParticipantAuthProfile) => {
    signedInDuringBootstrap.current = true
    // Nothing the previous account fetched may survive into this one. Query keys are
    // global (`['my-profile']`, the cart, notifications), so without this a sign-in
    // that lands inside the 30s `staleTime` is served the *previous* user's data with
    // no refetch — and `AppShell` then writes that stale profile back into auth state,
    // showing the wrong name and email for an account you are not signed in as.
    queryClient.clear()
    // The question bootstrapping asks — "is there a session?" — is now answered, so
    // stop waiting on the in-flight refresh. Letting it flip the flag later would
    // swap the gate from spinner to content underneath a user already interacting.
    setBootstrapping(false)
    const activeRole = participant.roles[0] ?? null
    // No refresh token here by design — it arrived as an httpOnly cookie the
    // browser stores and this code can never read (design/40 §4 P3).
    setSession(accessToken, participant.participant_id, participant.roles, activeRole)
    persistAuthState({
      participant,
      activeRole,
    })
    setState({
      token: accessToken,
      participant,
      roles: participant.roles,
      activeRole,
    })
  }, [])

  const logout = useCallback(async () => {
    try {
      // No body: the cookie identifies the session, and `credentials` is what sends
      // it. The server revokes the row and clears the cookie on the response.
      await fetch(apiUrl('api/v1/auth/logout'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })
    } catch {
      // best-effort logout
    }
    clearApiSession()
    clearStoredAuthState()
    // Signing out must not leave one user's orders, cart or profile in the cache for
    // whoever uses this browser next.
    queryClient.clear()
    setState(initialState)
  }, [])

  const switchRole = useCallback((role: string) => {
    setState((prev) => {
      if (prev.participant) {
        persistAuthState({
          participant: prev.participant,
          activeRole: role,
        })
      }
      setActiveSessionRole(role)
      return { ...prev, activeRole: role }
    })
  }, [])

  const syncParticipant = useCallback((participant: ParticipantAuthProfile) => {
    setState((prev) => {
      const nextRoles = participant.roles
      const nextActiveRole =
        prev.activeRole && nextRoles.includes(prev.activeRole)
          ? prev.activeRole
          : (nextRoles[0] ?? null)
      if (prev.token) {
        setSession(prev.token, participant.participant_id, nextRoles, nextActiveRole)
      }
      persistAuthState({
        participant,
        activeRole: nextActiveRole,
      })
      return {
        ...prev,
        participant,
        roles: nextRoles,
        activeRole: nextActiveRole,
      }
    })
  }, [])

  useEffect(() => {
    setActiveSessionRole(state.activeRole)
  }, [state.activeRole])

  const adoptRestoredSession = useCallback(() => {
    const stored = readStoredAuthState()
    const participantId = getParticipantId()
    const roles = getSessionRoles()
    const participant =
      stored?.participant && stored.participant.participant_id === participantId
        ? stored.participant
        : null
    const activeRole =
      stored?.activeRole && roles.includes(stored.activeRole)
        ? stored.activeRole
        : (roles[0] ?? null)
    setActiveSessionRole(activeRole)
    setState({
      token: getSessionToken(),
      participant,
      roles: participant?.roles ?? roles,
      activeRole,
    })
  }, [])

  // One silent refresh on mount. The stored profile is identity, not a credential,
  // so it is safe in localStorage and lets the shell render the right role
  // immediately instead of flashing a signed-out layout first.
  useEffect(() => {
    let cancelled = false
    let retryOnline: (() => void) | null = null

    const apply = (outcome: RefreshOutcome) => {
      if (cancelled || signedInDuringBootstrap.current) return
      if (outcome === 'restored') {
        adoptRestoredSession()
      } else if (outcome === 'no-session') {
        // The server ruled: this session is over. Dropping the stored profile keeps
        // a stale name and role from being shown to whoever signs in next.
        clearStoredAuthState()
      } else {
        // Unreachable. The session may be perfectly valid — we simply could not ask,
        // which on a patchy connection is the likely explanation. Keep the stored
        // profile so the next successful attempt restores the user instead of
        // presenting them as a stranger, and try again when the browser says it is
        // back online (a reload works too).
        retryOnline = () => {
          void bootstrapSession(0).then(apply)
        }
        window.addEventListener('online', retryOnline, { once: true })
      }
      setBootstrapping(false)
    }

    void bootstrapSession().then(apply)

    return () => {
      cancelled = true
      if (retryOnline) window.removeEventListener('online', retryOnline)
    }
  }, [adoptRestoredSession])

  return {
    ...state,
    login,
    logout,
    switchRole,
    syncParticipant,
    isAuthenticated: state.token !== null,
    isBootstrapping,
  }
}
